import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';

import {
  FormlyService,
  FormatService,
  LanguageService,
  MenuService,
  UtilityService,
  GridService,
  FormlyHideExpression,
  FormlyProps,
  FormlyExpressionProperties,
  IdDescription,
  WarpSpec,
} from '../../../tms-shared';
import {
  AddressService,
  CustomerAddressService,
  CustomerService,
  ProductService,
  SupplierShipperService,
  SupplierAddressService,
  SupplierService,
} from '../edit-create';
import { Address, Customer, CustomerAddress, Product, Supplier, SupplierAddress } from '../../models';
import { AddressCreateModalComponent } from '../../components/address/address-create-modal/address-create-modal.component';
import { CustomerAddressCreateModalComponent } from '../../components/address/customer-address-create-modal/customer-address-create-modal.component';
import { SupplierAddressCreateModalComponent } from '../../components/address/supplier-address-create-modal/supplier-address-create-modal.component';
import { ApplicationSettings } from '../../constants';

@Injectable({
  providedIn: 'root',
})
export class TmsFormlyService extends FormlyService {
  constructor(
    @Inject(DOCUMENT) protected document: Document,
    protected formatService: FormatService,
    protected menuService: MenuService,
    protected utilityService: UtilityService,
    protected languageService: LanguageService,
    private gridService: GridService,
    private customerService: CustomerService,
    private supplierService: SupplierService,
    private supplierShipperService: SupplierShipperService,
    private addressService: AddressService,
    private customerAddressService: CustomerAddressService,
    private supplierAddressService: SupplierAddressService,
    private productService: ProductService,
  ) {
    super(document, formatService, menuService, utilityService, languageService);
  }

  readonly addressSearchGridColumnDefs: ColDef[] = [
    this.gridService.text('addressee', $localize`Addressee`),
    this.gridService.text('name', $localize`Name`),
    this.gridService.text('line1', $localize`Line 1`),
    this.gridService.text('line2', $localize`Line 2`),
    this.gridService.text('line3', $localize`Line 3`),
    this.gridService.text('line4', $localize`Line 4`),
    this.gridService.text('townCity', $localize`Town/City`),
    this.gridService.text('region', $localize`Region`),
    this.gridService.text('postCode', $localize`Postcode`),
    this.gridService.text('countryName', $localize`Country`),
    this.gridService.text('phone', $localize`Telephone`),
    this.gridService.text('email', $localize`Email`),
    this.gridService.text('vatRegNo', $localize`VAT Number`),
  ];

  readonly customerSupplierGridColumnDefs: ColDef[] = [
    this.gridService.text('key', $localize`Key`),
    this.gridService.text('name', $localize`Name`),
    this.gridService.text('line1', $localize`Line 1`),
    this.gridService.text('line2', $localize`Line 2`),
    this.gridService.text('line3', $localize`Line 3`),
    this.gridService.text('line4', $localize`Line 4`),
    this.gridService.text('townCity', $localize`Town/City`),
    this.gridService.text('region', $localize`Region`),
    this.gridService.text('postCode', $localize`Postcode`),
    this.gridService.text('countryName', $localize`Country`),
    this.gridService.text('tel', $localize`Telephone`),
    this.gridService.text('fax', $localize`Fax`),
    this.gridService.text('mobileNo', $localize`Mobile`),
    this.gridService.text('email', $localize`Email`),
    this.gridService.text('web', $localize`Website`),
    this.gridService.text('vatRegNo', $localize`VAT Number`),
  ];

  readonly productGridColumnDefs: ColDef[] = [
    this.gridService.text('key', $localize`Key`),
    this.gridService.text('shortDescription', $localize`Short Description`),
    this.gridService.text('description', $localize`Description`),
    this.gridService.text('oldCode', $localize`Alternate Code`),
    this.gridService.text('partNumber', $localize`Part No`),
    this.gridService.text('productType', $localize`Product Type`),
    this.gridService.text('clearanceProduct', $localize`Clearance Product`),
  ];

  warpWeftPlan(
    key: string,
    label?: string,
    editable?: (model: WarpSpec, row: number) => boolean,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig[] {
    return [
      {
        key,
        type: 'warp-weft-plan',
        props: {
          label,
          editable,
          ...props,
        },
        expressions: {
          hide: hideExpression,
          ...expressionProperties,
        },
      },
      { key: key + 'Repeats' },
      { key: 'code' },
    ];
  }

  addressLookup(
    key: string,
    countries: Observable<IdDescription[]>,
    label?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'address-lookup',
      props: {
        label: label ?? $localize`Find Address`,
        countries,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  productSearch(
    key: string,
    validationKey: string,
    selected: (model: Product) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    id?: (model: any) => any,
    id2?: (model: any) => any,
  ): FormlyFieldConfig[] {
    const modalSearch = this.modalSearch(
      key,
      $localize`Find Product`,
      this.productService,
      this.productGridColumnDefs,
      id,
      undefined,
      undefined,
      selected,
      clear,
      { ...props, blankSearchSetting: ApplicationSettings.allowBlankProductSearch },
      hideExpression,
      id2,
    );

    return props?.required
      ? [modalSearch, this.requiredFieldValidation(validationKey, $localize`Product`)]
      : [modalSearch];
  }

  customerSearch(
    key: string,
    validationKey: string,
    label: string,
    selected: (model: Customer) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    const modalSearch = this.modalSearch(
      key,
      label ?? $localize`Find Customer`,
      this.customerService,
      this.customerSupplierGridColumnDefs,
      undefined,
      undefined,
      undefined,
      selected,
      clear,
      props,
      hideExpression,
    );

    return props?.required
      ? [modalSearch, this.requiredFieldValidation(validationKey, $localize`Customer`)]
      : [modalSearch];
  }

  supplierSearch(
    key: string,
    validationKey: string,
    label: string,
    selected: (model: Supplier) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    const modalSearch = this.modalSearch(
      key,
      label ?? $localize`Find Supplier`,
      this.supplierService,
      this.customerSupplierGridColumnDefs,
      undefined,
      undefined,
      undefined,
      selected,
      clear,
      props,
      hideExpression,
    );

    return props?.required
      ? [modalSearch, this.requiredFieldValidation(validationKey, $localize`Supplier`)]
      : [modalSearch];
  }

  supplierShipperSearch(
    key: string,
    validationKey: string,
    label: string,
    selected: (model: Supplier) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    const modalSearch = this.modalSearch(
      key,
      label ?? $localize`Find Shipper`,
      this.supplierShipperService,
      this.customerSupplierGridColumnDefs,
      undefined,
      undefined,
      undefined,
      selected,
      clear,
      props,
      hideExpression,
    );

    return props?.required
      ? [modalSearch, this.requiredFieldValidation(validationKey, $localize`Shipper`)]
      : [modalSearch];
  }

  addressSearch(
    key: string,
    multiLineKey: string,
    label: string,
    id: (model: any) => any,
    selected: (model: Address) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    return [
      this.modalSearch(
        key,
        label ?? $localize`Address`,
        this.addressService,
        this.addressSearchGridColumnDefs,
        id,
        $localize`All Addresses`,
        AddressCreateModalComponent,
        selected,
        clear,
        props,
        hideExpression,
      ),
      this.textarea(
        multiLineKey,
        label,
        undefined,
        {
          ...props,
          readonly: true,
          required: false,
          tabindex: -1,
        },
        hideExpression,
      ),
    ];
  }

  customerAddressSearch(
    key: string,
    multiLineKey: string,
    label: string,
    id: (model: any) => any,
    selected: (model: CustomerAddress) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    return [
      this.modalSearch(
        key,
        label ?? $localize`Customer Address`,
        this.customerAddressService,
        this.addressSearchGridColumnDefs,
        id,
        $localize`All Addresses`,
        CustomerAddressCreateModalComponent,
        selected,
        clear,
        props,
        hideExpression,
      ),
      this.textarea(
        multiLineKey,
        this.menuService.isReadOnly ? label : '',
        undefined,
        {
          ...props,
          readonly: true,
          required: false,
          tabindex: -1,
        },
        hideExpression,
      ),
    ];
  }

  supplierAddressSearch(
    key: string,
    multiLineKey: string,
    label: string,
    id: (model: any) => any,
    selected: (model: SupplierAddress) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    return [
      this.modalSearch(
        key,
        label ?? $localize`Supplier Address`,
        this.supplierAddressService,
        this.addressSearchGridColumnDefs,
        id,
        $localize`All Addresses`,
        SupplierAddressCreateModalComponent,
        selected,
        clear,
        props,
        hideExpression,
      ),
      this.textarea(
        multiLineKey,
        this.menuService.isReadOnly ? label : '',
        undefined,
        {
          ...props,
          readonly: true,
          required: false,
          tabindex: -1,
        },
        hideExpression,
      ),
    ];
  }
}
